// src/pages/NewTicketPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useDebounce } from '../hooks/useDebounce';
import AIAssistantPanel from '../components/AIAssistantPanel';
import { API_BASE } from '../utils/api';
import { Bot, Search, Paperclip, ChevronLeft, X } from 'lucide-react';

export default function NewTicketPage({ showToast }) {
  const [formData, setFormData] = useState({ title: '', description: '', priority: 'Low', category: 'Software', os: 'Windows', ticket_type: 'Incident' });
  const [attachments, setAttachments] = useState([]);
  const [aiData, setAiData] = useState(null);
  const [step, setStep] = useState(1);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [duplicates, setDuplicates] = useState([]);
  const [commonIssues, setCommonIssues] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [lastAiOutput, setLastAiOutput] = useState("");
  
  const [ccUsers, setCcUsers] = useState([]);
  const [ccSearch, setCcSearch] = useState('');
  const [ccResults, setCcResults] = useState([]);
  const [isCcSearching, setIsCcSearching] = useState(false);
  const navigate = useNavigate();
  const { request, loading } = useApi();

  const debouncedTitle = useDebounce(formData.title, 800);

  // Keyword Groups for balanced detection
  const KEYWORD_GROUPS = {
    Network: ["network", "ip", "dns", "vpn", "latency", "wifi", "internet", "connectivity"],
    Hardware: ["printer", "keyboard", "mouse", "monitor", "screen", "cable", "hardware"],
    Software: ["app", "error", "crash", "install", "software", "bug", "portal"],
    Access: ["login", "password", "access", "permission", "account", "mfa", "credentials"]
  };

  // Keyword-based AI auto-fill logic with high-confidence detection (2+ matches)
  useEffect(() => {
    const text = (formData.title + ' ' + formData.description).toLowerCase();
    if (!formData.title.trim() || !formData.description.trim()) {
      setAiSuggestion(null);
      setAiData(null); // Clear AI output when either input is cleared
      return;
    }

    if (text.length < 5) {
      setAiSuggestion(null);
      return;
    }

    let bestMatch = null;
    let maxMatches = 0;

    Object.entries(KEYWORD_GROUPS).forEach(([group, keywords]) => {
      const matches = keywords.filter(k => text.includes(k));
      if (matches.length >= 1 && matches.length > maxMatches) {
        maxMatches = matches.length;
        bestMatch = {
          category: group,
          priority: group === 'Access' || group === 'Network' ? 'High' : 'Medium',
          ticket_type: group === 'Access' || group === 'Hardware' ? 'Service Request' : 'Incident'
        };
      }
    });

    // Add high-priority detection for critical keywords
    if (text.includes('critical') || text.includes('emergency') || text.includes('down') || text.includes('urgent')) {
      if (bestMatch) bestMatch.priority = 'Critical';
    }

    // Only show suggestion if confidence is reasonable or title/description has substantial length
    if (bestMatch && (maxMatches >= 2 || formData.title.length > 20 || formData.description.length > 30)) {
      setAiSuggestion(bestMatch);
    } else {
      setAiSuggestion(null);
    }
  }, [formData.title, formData.description]);
  
  /* CC Search logic */
  useEffect(() => {
    if (ccSearch.trim().length < 2) {
      setCcResults([]);
      return;
    }
    const delayDebounce = setTimeout(async () => {
      setIsCcSearching(true);
      try {
        const res = await request(`/api/users/search?q=${encodeURIComponent(ccSearch)}`);
        const validRes = (res || []).filter(u => {
          if (!u) return false;
          if (u.is_internal) return false;
          if (u.is_active === false) return false;
          if (u.is_deleted) return false;
          const uname = (u.username || u.name || '').toLowerCase();
          if (uname === 'admin' || uname === 'system' || uname === 'root' || uname === 'recovery' || uname === 'breakglass' || uname.includes('protected')) return false;
          const uemail = (u.email || '').toLowerCase();
          if (uemail.startsWith('admin@') || uemail.startsWith('system@') || uemail.startsWith('root@') || uemail.startsWith('administrator@') || uemail.includes('recovery') || uemail.includes('breakglass')) return false;
          const uid = String(u.id || '').toLowerCase();
          if (uid === 'admin' || uid === 'system' || uid === 'root' || uid === 'administrator' || uid === '1') return false;
          return true;
        });
        setCcResults(validRes);
      } catch (err) {
        console.error('CC search failed:', err);
      } finally {
        setIsCcSearching(false);
      }
    }, 300);
    return () => clearTimeout(delayDebounce);
  }, [ccSearch, request]);

  const handleAddCc = (user) => {
    if (!ccUsers.find(u => u.id === user.id)) {
      setCcUsers([...ccUsers, user]);
    }
    setCcSearch('');
    setCcResults([]);
  };

  const handleRemoveCc = (id) => {
    setCcUsers(ccUsers.filter(u => u.id !== id));
  };

  const applySuggestion = () => {
    if (aiSuggestion) {
      setFormData(prev => ({
        ...prev,
        ...aiSuggestion
      }));
      setAiSuggestion(null);
      showToast("AI Suggestions applied!", "success");
    }
  };

  const handleGetFix = async () => {
    const input = formData.title + ' ' + formData.description;
    if (!input.trim()) { showToast("Please enter issue", "error"); return; }
    setAiLoading(true);
    try {
      const res = await request('/api/ai/get-fix', { method: 'POST', body: JSON.stringify({ issue: input }) });
      if (res && res.result) {
        try {
          const parsed = typeof res.result === 'string' ? JSON.parse(res.result) : res.result;
          setAiData({
            problem_summary: parsed.summary,
            suggested_fix: parsed.steps,
            if_persists: parsed.tips?.join('\n')
          });
        } catch {
          setAiData({ problem_summary: res.result });
        }
      } else {
        console.error("No result in response [Get Fix]:", res);
        showToast("No response from AI", 'error');
      }
    } catch (e) {
      console.error("Backend error [Get Fix]:", e);
      showToast(e.message || "AI request failed", 'error');
    }
    finally { setAiLoading(false); }
  };

  const handleDiagnose = async () => {
    const input = formData.title + ' ' + formData.description;
    if (!input.trim()) { showToast("Please enter issue", "error"); return; }
    setAiLoading(true);
    try {
      const res = await request('/api/ai/diagnose', { method: 'POST', body: JSON.stringify({ issue: input }) });
      if (res && res.result) {
        try {
          const parsed = typeof res.result === 'string' ? JSON.parse(res.result) : res.result;
          setAiData({
            problem_summary: parsed.summary,
            possible_causes: parsed.steps,
            if_persists: parsed.tips?.join('\n')
          });
        } catch {
          setAiData({ problem_summary: res.result });
        }
      } else {
        console.error("No result in response [Diagnose]:", res);
        showToast("No response from AI", 'error');
      }
    } catch (e) {
      console.error("Backend error [Diagnose]:", e);
      showToast(e.message || "AI request failed", 'error');
    }
    finally { setAiLoading(false); }
  };

  const handleImproveDescription = async () => {
    const textarea = document.getElementById("description-input");
    const currentText = textarea ? textarea.value : (formData.description || "");
    const cleanText = currentText.replace(/\s+/g, " ").trim();


    if (!cleanText || cleanText.length < 3) {
      return;
    }

    if (aiLoading) return;
    setAiLoading(true);
    try {
      const res = await request('/api/ai/improve-description', {
        method: 'POST',
        body: JSON.stringify({
          text: cleanText,
          variationId: Date.now()
        })
      });

      let improved = cleanText;
      if (res && res.result) {
        try {
          const parsed = typeof res.result === 'string' ? JSON.parse(res.result) : res.result;
          improved = parsed.steps?.join('\n') || parsed.summary || res.result;
        } catch {
          improved = res.result;
        }
      }
      setLastAiOutput(improved);
      setFormData(prev => ({ ...prev, description: improved }));
    } catch (e) {
      console.error("Improve failed:", e);
      setFormData(prev => ({ ...prev, description: cleanText }));
    } finally {
      setAiLoading(false);
    }
  };

  // Duplicate detection trigger
  useEffect(() => {
    if (!debouncedTitle || debouncedTitle.length < 5) {
      setDuplicates([]);
      return;
    }
    request(`/api/ai/duplicates?q=${encodeURIComponent(debouncedTitle)}`)
      .then(setDuplicates)
      .catch(() => { });
  }, [debouncedTitle, request]);

  // Fetch common issues
  useEffect(() => {
    request('/api/common-issues')
      .then(data => setCommonIssues(data || []))
      .catch(() => { });
  }, [request]);

  const handleCommonIssueClick = (issue) => {
    setFormData({
      ...formData,
      title: issue.title,
      description: issue.description || formData.description,
      category: issue.category || formData.category
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formPayload = new FormData();
      formPayload.append('title', formData.title);
      formPayload.append('description', formData.description);
      formPayload.append('category', aiData?.category || formData.category);
      formPayload.append('priority', aiData?.severity || formData.priority);
      formPayload.append('affected_system', aiData?.affected_system || 'Other');
      formPayload.append('os', formData.os);
      formPayload.append('ticket_type', formData.ticket_type);
      formPayload.append('ai_suggested', !!aiData);

      attachments.forEach(file => {
        formPayload.append('attachments', file);
      });

      if (ccUsers.length > 0) {
        formPayload.append('cc_users', JSON.stringify(ccUsers));
      }

      const resData = await request('/api/tickets', {
        method: 'POST',
        body: formPayload
      });

      showToast('Ticket created successfully', 'success');
      navigate(`/tickets/${resData.id}`);
    } catch (err) {
      showToast(err.message || 'Failed to submit ticket', 'error');
    }
  };

  const handleAiResolution = async (solved) => {
    if (solved) {
      try {
        await request('/api/ai/resolve-log', {
          method: 'POST',
          body: JSON.stringify({
            title: formData.title,
            description: formData.description,
            ai_response: aiData
          })
        });
        showToast('Glad to hear it worked! No ticket was created.', 'success');
        navigate('/dashboard');
      } catch (err) { }
    } else {
      showToast('Understood. Please proceed with submitting your ticket.', 'default');
    }
  };

  // Step 1 Validation
  const isStep1Valid = formData.title.length >= 5 && formData.description.length >= 10;

  return (
    <div className="new-ticket-container" style={{ padding: '24px' }}>
      <div className="new-ticket-main">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <h2 className="tcp-section-title">Report an Issue</h2>
          <button onClick={() => navigate(-1)} className="nav-icon-btn" title="Go Back">
            <ChevronLeft size={20} />
          </button>
        </div>

        {/* ─── Progress Indicator ─── */}
        <div className="tcp-progress-container" style={{ display: 'flex', alignItems: 'center', marginBottom: 40, background: 'var(--white-03)', padding: '16px 24px', borderRadius: 16, border: '1px solid var(--white-05)' }}>
          <div className={`tcp-step ${step === 1 ? 'tcp-step-active' : ''} ${step > 1 ? 'tcp-step-done' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="tcp-step-num" style={{ width: 32, height: 32, borderRadius: '50%', background: step >= 1 ? 'var(--text-60a5fa)' : 'var(--white-06)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#fff', boxShadow: step === 1 ? '0 0 15px rgba(96, 165, 250, 0.4)' : 'none' }}>{step > 1 ? '✓' : '1'}</div>
            <span style={{ fontSize: 14, fontWeight: step === 1 ? 700 : 500, color: step === 1 ? 'var(--text-primary)' : 'var(--text-muted)' }}>Step 1: Describe</span>
          </div>
          <div style={{ flex: 1, height: 2, background: 'var(--white-04)', margin: '0 24px', position: 'relative' }}>
            <div style={{ width: step > 1 ? '100%' : '0%', height: '100%', background: 'var(--text-60a5fa)', transition: 'width 0.4s ease' }}></div>
          </div>
          <div className={`tcp-step ${step === 2 ? 'tcp-step-active' : ''}`} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="tcp-step-num" style={{ width: 32, height: 32, borderRadius: '50%', background: step === 2 ? 'var(--text-60a5fa)' : 'var(--white-06)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 800, color: '#fff', boxShadow: step === 2 ? '0 0 15px rgba(96, 165, 250, 0.4)' : 'none' }}>2</div>
            <span style={{ fontSize: 14, fontWeight: step === 2 ? 700 : 500, color: step === 2 ? 'var(--text-primary)' : 'var(--text-muted)' }}>Step 2: Details</span>
          </div>
        </div>

        <form className="tcp-form-container" onSubmit={handleSubmit}>

          {step === 1 && (
            <div className="tcp-step-content">
              <div className="tcp-field-group" style={{ marginBottom: 32 }}>
                <label className="tcp-label" style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12, display: 'block' }}>Issue Title</label>
                <input
                  type="text"
                  className="tcp-input"
                  placeholder="What's happening? (e.g. Cannot access VPN)"
                  value={formData.title}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (!val.trim()) {
                      setFormData({ ...formData, title: val, description: "" });
                      setAiSuggestion(null);
                      setAiData(null);
                    } else {
                      setFormData({ ...formData, title: val });
                    }
                  }}
                  autoFocus
                  style={{ width: '100%', height: 54, padding: '0 20px', borderRadius: 14, outline: 'none', transition: 'all 0.2s' }}
                />
                <div className={`tcp-validation-info ${formData.title.length >= 5 ? 'tcp-val-valid' : 'tcp-val-neutral'}`} style={{ marginTop: 10, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6, color: formData.title.length >= 5 ? 'var(--text-3b82f6)' : 'var(--text-muted)' }}>
                  {formData.title.length >= 5 ? (
                    <><Search size={14} /> Title looks good ✓</>
                  ) : (
                    'Add a few more details to the title'
                  )}
                </div>

                {!formData.title && commonIssues.length > 0 && (
                  <div className="mt-6">
                    <div className="flex items-center gap-2 mb-3">
                      <Bot size={14} className="text-blue-500" />
                      <span className="text-[11px] text-slate-500 font-bold uppercase tracking-widest block">Suggested Issues:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {commonIssues.slice(0, 4).map((issue, idx) => (
                        <button
                          key={idx}
                          type="button"
                          className="tcp-pill-chip"
                          onClick={() => handleCommonIssueClick(issue)}
                        >
                          {issue.title}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="tcp-field-group" style={{ marginBottom: 32 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <label className="tcp-label" style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 0 }}>Description</label>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button type="button" onClick={handleDiagnose} disabled={aiLoading} className="tcp-btn-secondary" style={{ padding: '8px 16px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: 8, borderRadius: '20px', background: 'rgba(96, 165, 250, 0.1)', border: '1px solid rgba(96, 165, 250, 0.2)', color: 'var(--text-60a5fa)', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}>
                      {aiLoading ? <span className="animate-spin">○</span> : <Bot size={14} />}
                      Diagnose Issue
                    </button>
                    <button type="button" onClick={handleImproveDescription} disabled={aiLoading} className="tcp-btn-secondary" style={{ padding: '8px 16px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: 8, borderRadius: '20px', background: 'var(--white-05)', border: '1px solid var(--white-06)', color: 'var(--text-primary)', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}>
                      {aiLoading ? 'Improving...' : 'Improve Description'}
                    </button>
                  </div>
                </div>
                <textarea
                  id="description-input"
                  className="tcp-textarea"
                  placeholder="Explain the issue... e.g. what happened, when it started, and any error message you see."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  style={{ width: '100%', minHeight: '180px', padding: '20px', borderRadius: 14, outline: 'none', transition: 'all 0.2s', resize: 'none' }}
                />
                <div className="tcp-val-hint" style={{ marginTop: 12, padding: '12px 16px', background: 'rgba(96, 165, 250, 0.05)', border: '1px solid rgba(96, 165, 250, 0.1)', borderRadius: 10, fontSize: 13, color: 'var(--white-70)', lineHeight: 1.5 }}>
                  <div className="flex items-center gap-2 mb-1 font-bold text-[var(--text-60a5fa)]" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-60a5fa)', fontWeight: 800, fontSize: 11, textTransform: 'uppercase', marginBottom: 4 }}>
                    <Bot size={14} /> AI Recommendation
                  </div>
                  Tip: Briefly mention the system affected and any recent changes for a 30% faster resolution.
                </div>
              </div>

              <div className="mt-10 flex justify-end">
                <button
                  type="button"
                  className="tcp-btn-primary"
                  disabled={!isStep1Valid}
                  onClick={() => setStep(2)}
                  style={{ padding: '12px 32px', borderRadius: '12px', background: isStep1Valid ? 'var(--text-primary)' : 'var(--white-06)' }}
                >
                  Continue to Details &rarr;
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="tcp-step-content">
              {/* Smart AI Suggestion Bar */}
              {aiSuggestion && (
                <div className="tcp-ai-suggestion-bar">
                  <Bot size={18} color="var(--text-60a5fa)" />
                  <span style={{ fontSize: '13px', color: '#e2e8f0', fontWeight: 500 }}>
                    AI Note: Suggested <strong style={{ color: '#60a5fa' }}>{aiSuggestion.category}</strong> category & <strong style={{ color: '#60a5fa' }}>{aiSuggestion.priority}</strong> priority.
                  </span>
                  <button type="button" className="tcp-suggestion-apply" onClick={applySuggestion}>
                    Apply Suggestion
                  </button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-6">
                <div className="tcp-field-group">
                  <label className="tcp-label">Ticket Type</label>
                  <select className="tcp-select" value={formData.ticket_type} onChange={(e) => setFormData({ ...formData, ticket_type: e.target.value })}>
                    <option value="Incident">Incident</option>
                    <option value="Service Request">Service Request</option>
                  </select>
                </div>
                <div className="tcp-field-group">
                  <label className="tcp-label">Operating System</label>
                  <select className="tcp-select" value={formData.os} onChange={(e) => setFormData({ ...formData, os: e.target.value })}>
                    <option value="Windows">Windows</option>
                    <option value="macOS">macOS</option>
                    <option value="Linux">Linux</option>
                    <option value="Other">Other / N/A</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="tcp-field-group">
                  <label className="tcp-label">Priority</label>
                  <select
                    className="tcp-select"
                    value={formData.priority}
                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  >
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                    <option value="Critical">Critical</option>
                  </select>
                </div>
                <div className="tcp-field-group">
                  <label className="tcp-label">Category</label>
                  <select
                    className="tcp-select"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  >
                    <option value="Software">Software</option>
                    <option value="Hardware">Hardware</option>
                    <option value="Network">Network</option>
                    <option value="VPN">VPN</option>
                    <option value="Access">Access</option>
                    <option value="Application">Internal App</option>
                    <option value="Security">Security</option>
                  </select>
                </div>
              </div>

              <div className="tcp-field-group" style={{ marginBottom: 32 }}>
                <label className="tcp-label" style={{ fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12, display: 'block' }}>Attachments <span style={{ fontWeight: 400, opacity: 0.6 }}>(optional)</span></label>
                <label className="tcp-file-input-wrapper" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '20px', borderRadius: 14, border: '1px dashed var(--white-20)', background: 'var(--white-02)', cursor: 'pointer', transition: 'all 0.2s' }}>
                  <Paperclip size={20} style={{ color: 'var(--text-60a5fa)' }} />
                  <span style={{ fontSize: 14, color: 'var(--white-60)' }}>
                    Click to select or drag files here
                  </span>
                  <input
                    type="file"
                    multiple
                    onChange={(e) => {
                      const filesArray = Array.from(e.target.files);
                      const allowedExts = ['.jpg', '.jpeg', '.png', '.pdf', '.docx', '.txt'];
                      const invalidFiles = filesArray.filter(file => {
                        const ext = file.name.slice(file.name.lastIndexOf('.')).toLowerCase();
                        return !allowedExts.includes(ext);
                      });
                      if (invalidFiles.length > 0) {
                        showToast(`File type not allowed. Only JPG, PNG, PDF, DOCX, and TXT files are permitted.`, 'error');
                        return;
                      }
                      setAttachments(prev => [...prev, ...filesArray]);
                    }}
                    style={{ display: 'none' }}
                  />
                </label>
                {attachments.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                    {attachments.map((file, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          background: 'rgba(255, 255, 255, 0.03)',
                          padding: '10px 16px',
                          borderRadius: '10px',
                          border: '1px solid var(--white-06)',
                          fontSize: '13px'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)' }}>
                          <Paperclip size={14} style={{ color: 'var(--text-60a5fa)', flexShrink: 0 }} />
                          <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '300px' }}>{file.name}</span>
                          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>({(file.size / 1024).toFixed(1)} KB)</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => setAttachments(attachments.filter((_, i) => i !== idx))}
                          style={{
                            border: 'none',
                            background: 'transparent',
                            color: 'var(--text-muted)',
                            cursor: 'pointer',
                            padding: 4,
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={e => {
                            e.currentTarget.style.color = '#f87171';
                            e.currentTarget.style.background = 'rgba(248, 113, 113, 0.1)';
                          }}
                          onMouseLeave={e => {
                            e.currentTarget.style.color = 'var(--text-muted)';
                            e.currentTarget.style.background = 'transparent';
                          }}
                          title="Remove attachment"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="tcp-field-group">
                <label className="tcp-label">CC WATCHERS (OPTIONAL)</label>
                <div style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '12px', background: 'var(--white-03)', borderRadius: 12, border: '1px solid var(--white-06)', minHeight: 50 }}>
                    {ccUsers.map(u => (
                      <span key={u.id} style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 6, 
                        background: 'rgba(96, 165, 250, 0.12)', 
                        color: 'var(--text-60a5fa)', 
                        padding: '4px 10px', 
                        borderRadius: '6px', 
                        fontSize: 12, 
                        fontWeight: 600,
                        border: '1px solid rgba(96, 165, 250, 0.2)'
                      }}>
                        {u.name}
                        <button 
                          type="button" 
                          onClick={() => handleRemoveCc(u.id)} 
                          style={{ 
                            border: 'none', 
                            background: 'transparent', 
                            color: 'rgba(96, 165, 250, 0.6)', 
                            cursor: 'pointer', 
                            display: 'flex', 
                            padding: 2,
                            borderRadius: '4px',
                            transition: 'all 0.15s ease'
                          }}
                          onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                          onMouseLeave={e => e.currentTarget.style.color = 'rgba(96, 165, 250, 0.6)'}
                        >
                          <X size={12} />
                        </button>
                      </span>
                    ))}
                    <input
                      type="text"
                      placeholder={ccUsers.length === 0 ? "Search AD users by name or email..." : ""}
                      value={ccSearch}
                      onChange={(e) => setCcSearch(e.target.value)}
                      style={{ flex: 1, background: 'transparent', border: 'none', color: 'var(--text-primary)', fontSize: 13, outline: 'none', minWidth: 150 }}
                    />
                  </div>
                  
                  {isCcSearching && <div style={{ position: 'absolute', right: 15, top: 15 }}><div className="spin" style={{ width: 16, height: 16, border: '2px solid var(--text-60a5fa)', borderTopColor: 'transparent', borderRadius: '50%' }}></div></div>}

                  {ccResults.length > 0 && (
                    <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100, background: 'var(--surface-3)', border: '1px solid var(--border-subtle)', borderRadius: 12, marginTop: 8, boxShadow: 'var(--shadow-lg)', maxHeight: 200, overflowY: 'auto' }}>
                      {ccResults.map(u => (
                        <div
                          key={u.id}
                          onClick={() => handleAddCc(u)}
                          style={{ padding: '10px 15px', cursor: 'pointer', borderBottom: '1px solid var(--white-05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                          onMouseEnter={(e) => e.target.style.background = 'var(--white-05)'}
                          onMouseLeave={(e) => e.target.style.background = 'transparent'}
                        >
                          <div>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{u.name}</div>
                            <div style={{ fontSize: 11, color: 'var(--white-50)' }}>{u.email}</div>
                          </div>
                          <div style={{ fontSize: 10, background: 'rgba(96, 165, 250, 0.1)', color: 'var(--text-60a5fa)', padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>AD</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="tcp-field-divider"></div>

              <div className="submit-container gap-4">
                <button type="button" className="tcp-btn-secondary" onClick={() => setStep(1)} style={{ padding: '0 24px', height: 50, borderRadius: 12, border: '1px solid var(--white-06)', background: 'var(--white-05)', color: 'var(--text-primary)', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>Back</button>
                <button type="submit" className="tcp-btn-primary" disabled={loading} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '60%', height: 50, borderRadius: 12, background: 'var(--text-60a5fa)', color: '#fff', border: 'none', fontSize: 15, fontWeight: 800, cursor: 'pointer', boxShadow: '0 4px 15px rgba(96, 165, 250, 0.3)', transition: 'all 0.2s' }}>
                  {loading ? 'Creating ticket...' : 'Submit Ticket'}
                </button>
              </div>
            </div>
          )}
        </form>

        
      </div>

      <div className="new-ticket-sidebar">
        <AIAssistantPanel
          data={aiData}
          loading={aiLoading}
          onResolve={handleAiResolution}
          aiSuggestion={aiSuggestion}
          onGetFix={handleGetFix}
          onDiagnose={handleDiagnose}
          onImprove={handleImproveDescription}
        />
      </div>
    </div>
  );
}
